import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Trash2, Save } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import UserAvatar from "../components/UserAvatar";

/**
 * ProfilePage (Phase 10 Task A) — usato sia per staff (/app/profilo) sia
 * per client portal (/portal/profilo). Layout identico.
 */
export default function ProfilePage() {
    const { user, refresh } = useAuth();
    const [profile, setProfile] = useState(null);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [birthday, setBirthday] = useState("");
    const [phone, setPhone] = useState("");
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [avatarBust, setAvatarBust] = useState(0);
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me/profile");
            setProfile(data);
            setFirstName(data.first_name || "");
            setLastName(data.last_name || "");
            setBirthday(data.birthday || "");
            setPhone(data.phone || "");
        } catch {
            toast.error("Errore caricamento profilo");
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onSave = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (saving) return;
        setSaving(true);
        try {
            const payload = {
                first_name: firstName.trim() || null,
                last_name: lastName.trim() || null,
                birthday: birthday.trim() || null,
                phone: phone.trim() || null,
            };
            await api.patch("/auth/me/profile", payload);
            toast.success("Profilo aggiornato");
            await load();
            if (typeof refresh === "function") await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    };

    const onPickFile = () => fileRef.current?.click();

    const onFileChange = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (!["image/jpeg", "image/png"].includes(f.type)) {
            toast.error("Solo JPG o PNG"); return;
        }
        if (f.size > 2 * 1024 * 1024) {
            toast.error("Immagine troppo grande (max 2 MB)"); return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            await api.post("/auth/me/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Foto aggiornata");
            setAvatarBust(Date.now());
            await load();
            if (typeof refresh === "function") await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload fallito");
        } finally {
            setUploading(false);
        }
    };

    const onDeletePhoto = async () => {
        if (!profile?.has_photo) return;
        setUploading(true);
        try {
            await api.delete("/auth/me/profile/photo");
            toast.success("Foto rimossa");
            setAvatarBust(Date.now());
            await load();
            if (typeof refresh === "function") await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore");
        } finally {
            setUploading(false);
        }
    };

    if (!profile) {
        return <div style={{ padding: 40, color: "#8A8F88" }}>Caricamento…</div>;
    }

    const displayName = [profile.first_name, profile.last_name].filter(Boolean).join(" ") || profile.name || profile.email;

    return (
        <div style={{ padding: "32px 40px", maxWidth: 720 }} data-testid="profile-page">
            <h1 className="lt-h1" style={{ marginBottom: 24 }}>Il mio profilo</h1>

            <div className="lt-card" style={{ padding: 24, marginBottom: 20 }} data-testid="profile-photo-section">
                <h2 className="lt-section-title">Foto profilo</h2>
                <div style={{ display: "flex", alignItems: "center", gap: 24, marginTop: 12 }}>
                    <UserAvatar
                        userId={profile.id}
                        name={displayName}
                        hasPhoto={profile.has_photo}
                        size={128}
                        cacheBust={avatarBust}
                    />
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/jpeg,image/png"
                            style={{ display: "none" }}
                            onChange={onFileChange}
                            data-testid="profile-photo-input"
                        />
                        <button
                            type="button"
                            className="lt-btn-secondary"
                            onClick={onPickFile}
                            disabled={uploading}
                            data-testid="profile-photo-change-btn"
                        >
                            <Camera size={14} /> {uploading ? "…" : (profile.has_photo ? "Cambia foto" : "Carica foto")}
                        </button>
                        {profile.has_photo && (
                            <button
                                type="button"
                                className="lt-btn-secondary"
                                onClick={onDeletePhoto}
                                disabled={uploading}
                                data-testid="profile-photo-delete-btn"
                            >
                                <Trash2 size={14} /> Rimuovi
                            </button>
                        )}
                    </div>
                </div>
                <div style={{ marginTop: 12, fontSize: 12, color: "#8A8F88" }}>
                    JPG o PNG, max 2 MB. L&apos;immagine verrà normalizzata a 256×256 px.
                </div>
            </div>

            <form onSubmit={onSave} method="post" action="#" noValidate className="lt-card" style={{ padding: 24 }} data-testid="profile-form">
                <h2 className="lt-section-title">Dati personali</h2>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                    <div>
                        <label className="lt-label">Nome</label>
                        <input className="lt-field" value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={100} data-testid="profile-first-name" />
                    </div>
                    <div>
                        <label className="lt-label">Cognome</label>
                        <input className="lt-field" value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={100} data-testid="profile-last-name" />
                    </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                    <div>
                        <label className="lt-label">Data di nascita</label>
                        <input type="date" className="lt-field" value={birthday} onChange={(e) => setBirthday(e.target.value)} data-testid="profile-birthday" />
                    </div>
                    <div>
                        <label className="lt-label">Telefono</label>
                        <input className="lt-field" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} placeholder="+39 340 1234567" data-testid="profile-phone" />
                    </div>
                </div>

                <label className="lt-label">Email (non modificabile)</label>
                <input className="lt-field" value={profile.email || ""} disabled data-testid="profile-email" />

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
                    <button type="submit" className="lt-btn-primary" disabled={saving} onClick={onSave} data-testid="profile-save-btn">
                        <Save size={14} /> {saving ? "Salvataggio…" : "Salva modifiche"}
                    </button>
                </div>
            </form>
        </div>
    );
}
